import React, { useState, useEffect } from 'react';
import axios from 'axios';

const AdminPage = () => {
  const [users, setUsers] = useState([]);
  const [films, setFilms] = useState([]); // State for films
  const [reviews, setReviews] = useState([]); // State for reviews
  const [transactions, setTransactions] = useState([]); // State for transactions
  const [stats, setStats] = useState(null); // State to store stats
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // State for search and filter criteria
  const [userSearch, setUserSearch] = useState('');
  const [filmSearch, setFilmSearch] = useState('');
  const [reviewSearch, setReviewSearch] = useState('');
  const [filmGenreFilter, setFilmGenreFilter] = useState(''); // Filter by genre
  const [feedbackCategoryFilter, setFeedbackCategoryFilter] = useState(''); // Filter by feedback category

  // State for toggling deactivated users and hidden reviews
  const [includeDeactivated, setIncludeDeactivated] = useState(false);
  const [includeHiddenReviews, setIncludeHiddenReviews] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('token'); // JWT token for authentication

      try {
        // Fetch stats, users, films, and reviews concurrently
        const [statsResponse, usersResponse, filmsResponse, reviewsResponse, transactionsResponse] = await Promise.all([
          axios.get('https://api.eatourshorts.com/admin/stats', {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`https://api.eatourshorts.com/admin/users?include_deactivated=${includeDeactivated ? 'true' : 'false'}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get('https://api.eatourshorts.com/admin/films', {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(
            `https://api.eatourshorts.com/admin/reviews?page=${currentPage}&limit=10&include_hidden=${includeHiddenReviews}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }),
         axios.get('https://api.eatourshorts.com/admin/transactions', {
            headers: { Authorization: `Bearer ${token}` },
            }),
        ]);

        // Set state with fetched data
        setStats(statsResponse.data);
        setUsers(usersResponse.data);
        setFilms(filmsResponse.data);
        setReviews(reviewsResponse.data.reviews);
        setTotalPages(reviewsResponse.data.totalPages);
        setTransactions(transactionsResponse.data); // Set transactions data
        setError(''); // Clear any previous errors
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to fetch data.');
      }
    };

    fetchData();
  }, [currentPage, includeDeactivated, includeHiddenReviews]); // Refetch data when toggles or page changes

  const handlePromote = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(
        'https://api.eatourshorts.com/admin/promote',
        { user_id: userId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setSuccess(response.data.message);
      setError('');
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === userId ? { ...user, role: 'admin' } : user
        )
      );
    } catch (err) {
      console.error('Error promoting user:', err);
      setError('Failed to promote user.');
      setSuccess('');
    }
  };

  const handleDemote = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(
        'https://api.eatourshorts.com/admin/demote',
        { user_id: userId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setSuccess(response.data.message);
      setError('');
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === userId ? { ...user, role: 'user' } : user
        )
      );
    } catch (err) {
      console.error('Error demoting user:', err);
      setError('Failed to demote user.');
      setSuccess('');
    }
  };

  const handleDeactivateUser = async (userId) => {
    try {
      const token = localStorage.getItem('token'); // JWT token for authentication
      const response = await axios.patch(
        `https://api.eatourshorts.com/admin/users/${userId}/deactivate`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setSuccess(response.data.message);
      setError('');
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === userId ? { ...user, is_active: false } : user
        )
      );
    } catch (err) {
      console.error('Error deactivating user:', err);
      setError('Failed to deactivate user.');
      setSuccess('');
    }
  };
  
  const handleActivateUser = async (userId) => {
    try {
      const token = localStorage.getItem('token'); // JWT token for authentication
      const response = await axios.patch(
        `https://api.eatourshorts.com/admin/users/${userId}/activate`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setSuccess(response.data.message);
      setError('');
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === userId ? { ...user, is_active: true } : user
        )
      );
    } catch (err) {
      console.error('Error activating user:', err);
      setError('Failed to activate user.');
      setSuccess('');
    }
  };

  const handleToggleFilmVisibility = async (filmId, currentVisibility) => {
    try {
      const token = localStorage.getItem('token'); // JWT token for authentication
      const newVisibility = currentVisibility === 'public' ? 'private' : 'public';
  
      const response = await axios.patch(
        `https://api.eatourshorts.com/admin/films/${filmId}/visibility`,
        { visibility: newVisibility },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
  
      setSuccess(response.data.message);
      setError('');
      setFilms((prevFilms) =>
        prevFilms.map((film) =>
          film.id === filmId ? { ...film, visibility: newVisibility } : film
        )
      );
    } catch (err) {
      console.error('Error toggling film visibility:', err);
      setError('Failed to toggle film visibility.');
      setSuccess('');
    }
  };

  const handleExportEmails = () => {
    if (users.length === 0) {
      setError('No users available to export.');
      return;
    }

    // Generate CSV content
    const csvContent =
      'data:text/csv;charset=utf-8,' +
      ['Username,Email'] // Add header row
        .concat(users.map((user) => `${user.username},${user.email}`)) // Add user data
        .join('\n'); // Join rows with newline

    // Create a downloadable link
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', 'user_emails.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Filtered data based on search and filter criteria
  const filteredUsers = users.filter((user) =>
    user.username.toLowerCase().includes(userSearch.toLowerCase()) ||
    user.email.toLowerCase().includes(userSearch.toLowerCase())
  );

  const filteredFilms = films.filter((film) =>
    (film.title?.toLowerCase().includes(filmSearch.toLowerCase()) ||     
  film.uploader_username?.toLowerCase().includes(filmSearch.toLowerCase())) &&
    (filmGenreFilter === '' || film.genre?.toLowerCase().includes(filmGenreFilter.toLowerCase())) &&
    (feedbackCategoryFilter === '' || film.feedback_categories?.includes(feedbackCategoryFilter))
  );

  const filteredReviews = reviews.filter((review) =>
    review.reviewer_username?.toLowerCase().includes(reviewSearch.toLowerCase()) ||
    review.review?.toLowerCase().includes(reviewSearch.toLowerCase())
  );

  return (
    <div style={{ padding: '20px' }}>
      <h1>Admin Dashboard</h1>
      {error && <p style={{ color: 'red' }}>{error}</p>}
      {success && <p style={{ color: 'green' }}>{success}</p>}
        {/* Transactions Section */}
        <h2>Transactions</h2>
            {transactions.length > 0 ? (
                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
                <thead>
                  <tr>
                    <th style={{ border: '1px solid #ccc', padding: '10px' }}>Transaction ID</th>
                    <th style={{ border: '1px solid #ccc', padding: '10px' }}>User</th>
                    <th style={{ border: '1px solid #ccc', padding: '10px' }}>Token Amount</th>
                    <th style={{ border: '1px solid #ccc', padding: '10px' }}>Amount Paid</th>
                    <th style={{ border: '1px solid #ccc', padding: '10px' }}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((transaction) => (
                    <tr key={transaction.transaction_id}>
                      <td style={{ border: '1px solid #ccc', padding: '10px' }}>{transaction.transaction_id}</td>
                      <td style={{ border: '1px solid #ccc', padding: '10px' }}>{transaction.user_email}</td>
                      <td style={{ border: '1px solid #ccc', padding: '10px' }}>{transaction.tokens}</td>
                      <td style={{ border: '1px solid #ccc', padding: '10px' }}>${transaction.amount}</td>
                      <td style={{ border: '1px solid #ccc', padding: '10px' }}>{new Date(transaction.date).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
                <p>No transactions found.</p>
            )}

      {/* Display Stats */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
        {stats ? (
          <>
            <div style={{ padding: '10px', border: '1px solid #ccc', borderRadius: '5px', width: '15%' }}>
              <h3>Total Users</h3>
              <p>{stats.total_users}</p>
            </div>
            <div style={{ padding: '10px', border: '1px solid #ccc', borderRadius: '5px', width: '15%' }}>
              <h3>Total Films Uploaded</h3>
              <p>{stats.total_films_uploaded}</p>
            </div>
            <div style={{ padding: '10px', border: '1px solid #ccc', borderRadius: '5px', width: '15%' }}>
              <h3>Total Films Public</h3>
              <p>{stats.total_films_public}</p>
            </div>
            <div style={{ padding: '10px', border: '1px solid #ccc', borderRadius: '5px', width: '15%' }}>
              <h3>Total Reviews Published</h3>
              <p>{stats.total_reviews_published}</p>
            </div>
            <div style={{ padding: '10px', border: '1px solid #ccc', borderRadius: '5px', width: '15%' }}>
              <h3>Total Reviews Pending</h3>
              <p>{stats.total_reviews_pending}</p>
            </div>
            <div style={{ padding: '10px', border: '1px solid #ccc', borderRadius: '5px', width: '15%' }}>
              <h3>Total Ratings</h3>
              <p>{stats.total_ratings}</p>
            </div>
            <div style={{ padding: '10px', border: '1px solid #ccc', borderRadius: '5px', width: '15%' }}>
              <h3>Total Tokens</h3>
              <p>{stats.total_tokens}</p>
            </div>
          </>
        ) : (
          <p>Loading stats...</p>
        )}
      </div>

      <h2>Users</h2>
<label>
  <input
    type="checkbox"
    checked={includeDeactivated}
    onChange={() => setIncludeDeactivated((prev) => !prev)}
    style={{ marginRight: '5px' }}
  />
  Include Deactivated Users
</label>
<input
  type="text"
  placeholder="Search users by username or email"
  value={userSearch}
  onChange={(e) => setUserSearch(e.target.value)}
  style={{ marginBottom: '10px', padding: '5px', width: '100%' }}
/>
<button
  onClick={handleExportEmails}
  style={{
    marginBottom: '10px',
    padding: '10px',
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
  }}
>
  Export Emails as CSV
</button>

{filteredUsers.length > 0 ? (
  <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
    <thead>
      <tr>
        <th style={{ border: '1px solid #ccc', padding: '10px' }}>Username</th>
        <th style={{ border: '1px solid #ccc', padding: '10px' }}>Email</th>
        <th style={{ border: '1px solid #ccc', padding: '10px' }}>Role</th>
        <th style={{ border: '1px solid #ccc', padding: '10px' }}>Tokens</th>
        <th style={{ border: '1px solid #ccc', padding: '10px' }}>Active</th>
        <th style={{ border: '1px solid #ccc', padding: '10px' }}>Actions</th>
      </tr>
    </thead>
    <tbody>
      {filteredUsers.map((user) => (
        <tr key={user.id}>
          <td style={{ border: '1px solid #ccc', padding: '10px' }}>{user.username}</td>
          <td style={{ border: '1px solid #ccc', padding: '10px' }}>{user.email}</td>
          <td style={{ border: '1px solid #ccc', padding: '10px' }}>{user.role}</td>
          <td style={{ border: '1px solid #ccc', padding: '10px' }}>{user.tokens}</td>
          <td style={{ border: '1px solid #ccc', padding: '10px' }}>
            {user.is_active ? 'Yes' : 'No'}
          </td>
          <td style={{ border: '1px solid #ccc', padding: '10px', textAlign: 'center' }}>
            {user.role !== 'admin' && (
              <button
                onClick={() => handlePromote(user.id)}
                style={{
                  marginRight: '5px',
                  padding: '5px 10px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                }}
              >
                Promote to Admin
              </button>
            )}
            {user.role === 'admin' && (
              <button
                onClick={() => handleDemote(user.id)}
                style={{
                  marginRight: '5px',
                  padding: '5px 10px',
                  backgroundColor: '#ffc107',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                }}
              >
                Demote to User
              </button>
            )}
            {user.is_active ? (
              <button
                onClick={() => handleDeactivateUser(user.id)}
                style={{
                  marginRight: '5px',
                  padding: '5px 10px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                }}
              >
                Deactivate
              </button>
            ) : (
              <button
                onClick={() => handleActivateUser(user.id)}
                style={{
                  padding: '5px 10px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                }}
              >
                Activate
              </button>
            )}
          </td>
        </tr>
      ))}
    </tbody>
  </table>
) : (
  <p>No users found.</p>
)}

      <h2>Films</h2>
<input
  type="text"
  placeholder="Search films by title and username"
  value={filmSearch}
  onChange={(e) => setFilmSearch(e.target.value)}
  style={{ marginBottom: '10px', padding: '5px', width: '100%' }}
/>
<input
  type="text"
  placeholder="Search by genre"
  value={filmGenreFilter}
  onChange={(e) => setFilmGenreFilter(e.target.value)}
  style={{ marginBottom: '10px', padding: '5px', width: '100%' }}
/>
<select
  value={feedbackCategoryFilter}
  onChange={(e) => setFeedbackCategoryFilter(e.target.value)}
  style={{ marginBottom: '10px', padding: '5px', width: '100%' }}
>
  <option value="">All Feedback Categories</option>
  <option value="Writing">Writing</option>
  <option value="Cinematography">Cinematography</option>
  <option value="Directing">Directing</option>
  <option value="Sound-Design">Sound-Design</option>
  <option value="Art-Design">Art-Design</option>
  <option value="Editing">Editing</option>
  <option value="Costuming">Costuming</option>
</select>

{filteredFilms.length > 0 ? (
  <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
    <thead>
      <tr>
        <th style={{ border: '1px solid #ccc', padding: '10px' }}>Title</th>
        <th style={{ border: '1px solid #ccc', padding: '10px' }}>Uploader</th>
        <th style={{ border: '1px solid #ccc', padding: '10px' }}>Genre</th>
        <th style={{ border: '1px solid #ccc', padding: '10px' }}>Feedback Categories</th>
        <th style={{ border: '1px solid #ccc', padding: '10px' }}>Visibility</th>
      </tr>
    </thead>
    <tbody>
      {filteredFilms.map((film) => (
        <tr key={film.id}>
          <td style={{ border: '1px solid #ccc', padding: '10px' }}>{film.title}</td>
          <td style={{ border: '1px solid #ccc', padding: '10px' }}>{film.uploader_username}</td>
          <td style={{ border: '1px solid #ccc', padding: '10px' }}>{film.genre}</td>
          <td style={{ border: '1px solid #ccc', padding: '10px' }}>
            {film.feedback_categories?.join(', ') || 'None'}
          </td>

          <td style={{ border: '1px solid #ccc', padding: '10px', textAlign: 'center' }}>
            <button
              onClick={() => handleToggleFilmVisibility(film.id, film.visibility)}
              style={{
                padding: '5px 10px',
                backgroundColor: film.visibility === 'public' ? 'green' : 'gray',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
              }}
            >
              {film.visibility === 'public' ? 'Public' : 'Private'}
            </button>
          </td>
        </tr>
      ))}
    </tbody>
  </table>
) : (
  <p>No films found.</p>
)}

      <h2>Reviews</h2>
      <button
        onClick={() => setIncludeHiddenReviews((prev) => !prev)}
        style={{
          marginBottom: '10px',
          padding: '10px',
          backgroundColor: '#007bff',
          color: 'white',
          border: 'none',
          borderRadius: '5px',
          cursor: 'pointer',
        }}
      >
        {includeHiddenReviews ? 'Hide Hidden Reviews' : 'Include Hidden Reviews'}
      </button>
      <input
        type="text"
        placeholder="Search reviews by reviewer, content, or username"
        value={reviewSearch}
        onChange={(e) => setReviewSearch(e.target.value)}
        style={{ marginBottom: '10px', padding: '5px', width: '100%' }}
      />
      <ul>
        {filteredReviews.map((review) => (
          <li key={review.id}>
            <strong>{review.reviewer_username}</strong>: {review.review}

          </li>
        ))}
      </ul>
      <div style={{ marginTop: '20px' }}>
        <button
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
          style={{ marginRight: '10px' }}
        >
          Previous
        </button>
        <span>
          Page {currentPage} of {totalPages}
        </span>
        <button
          onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
          disabled={currentPage === totalPages}
          style={{ marginLeft: '10px' }}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default AdminPage;